import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { usePermissions } from '../hooks/usePermissions'
import { useNotificationRefresh } from '../contexts/NotificationContext'
import { Heart, Calendar, FileText, Users, Building2, User, Clock, TrendingUp, RefreshCw, AlertCircle, Activity, Bell, ArrowRight } from 'lucide-react'
import { useDashboardData } from '../hooks/useDashboardData'
import StatsCard, { StatsGrid } from '../components/ui/StatsCard'
import Card from '../components/ui/Card'
import Button from '../components/ui/Button'
import Badge from '../components/ui/Badge'
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from 'chart.js'
import { Bar, Doughnut, Line } from 'react-chartjs-2'
import { labelEstadoMascota } from '../utils/mascotaLabels'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler
)

export default function Dashboard() {
  const { user } = useAuth()
  const { isDueno, isVeterinario, isSuperAdmin, isVeterinarioAdmin } = usePermissions()
  const { data, loading, error, refetch } = useDashboardData(user)
  const navigate = useNavigate()
  
  let contadorNoLeidas = 0
  try {
    const notificationsContext = useNotificationRefresh()
    if (!isDueno && notificationsContext) {
      contadorNoLeidas = notificationsContext.contadorNoLeidas || 0
    }
  } catch (err) {
    console.warn('Error obteniendo contador de notificaciones:', err)
    contadorNoLeidas = 0
  }

  const getGreeting = () => {
    const hour = new Date().getHours()
    if (hour < 12) return 'Buenos días'
    if (hour < 18) return 'Buenas tardes'
    return 'Buenas noches'
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <RefreshCw className="h-10 w-10 animate-spin mx-auto text-primary-600 dark:text-primary-400 mb-4" />
          <p className="text-pastel-ink/80 dark:text-gray-400">Cargando dashboard...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <AlertCircle className="h-10 w-10 mx-auto text-red-600 dark:text-red-400 mb-4" />
          <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
          <button 
            onClick={refetch}
            className="btn-primary"
          >
            Reintentar
          </button>
        </div>
      </div>
    )
  }

  // Validar que tenemos datos antes de renderizar
  if (!data || !data.resumen) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <AlertCircle className="h-10 w-10 mx-auto text-gray-400 dark:text-gray-500 mb-4" />
          <p className="text-pastel-ink/80 dark:text-gray-400 mb-4">No hay datos disponibles</p>
          <button 
            onClick={refetch}
            className="btn-primary"
          >
            Reintentar
          </button>
        </div>
      </div>
    )
  }

  const resumen = data?.resumen || {}
  const citasRecientes = Array.isArray(data?.citasRecientes) ? data.citasRecientes : []
  const mascotasRecientes = Array.isArray(data?.mascotasRecientes) ? data.mascotasRecientes : []
  const currentVet = user?.veterinarios?.[0] || {}
  const clinicName =
    currentVet?.clinica?.nombre ||
    currentVet?.clinica?.name ||
    currentVet?.nombre_clinica ||
    currentVet?.nombreClinica ||
    user?.clinica?.nombre ||
    'Mi clínica'
  const clinicAddress =
    currentVet?.clinica?.direccion ||
    currentVet?.clinica?.address ||
    currentVet?.direccion_clinica ||
    currentVet?.direccionClinica ||
    ''

  const miniBarData = [80, 95, 110, 218, 150, 130, 145, 160, 155, 170, 140, 185]
  const miniBarLabels = ['E', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D']

  const recentAppointments = citasRecientes.slice(0, 6).map((cita) => {
    const fecha = cita.fecha_cita_propuesta || cita.fechaCitaPropuesta
    const hora = cita.hora_cita_propuesta || cita.horaCitaPropuesta
    const mascotaNombre = cita?.mascota?.nombre || 'Mascota'
    const clinicName = cita?.clinica?.nombre || cita?.clinica?.name || 'Clínica'
    const vetName = cita?.veterinario?.usuario
      ? `${cita.veterinario.usuario.nombre || ''} ${cita.veterinario.usuario.apellido || ''}`.trim()
      : 'Veterinario'
    const estado = String(cita.estado || '').toUpperCase()

    let badgeVariant = 'info'
    let badgeText = 'Pendiente'
    if (estado.includes('COMPLET')) {
      badgeVariant = 'success'
      badgeText = 'Completada'
    } else if (estado.includes('APROBAD') || estado.includes('CONFIRM')) {
      badgeVariant = 'warning'
      badgeText = 'En curso'
    }

    return {
      id: cita.id_cita || cita.idCita,
      mascotaNombre,
      clinicName,
      vetName,
      hora: hora || (fecha ? new Date(fecha).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) : '--:--'),
      badgeVariant,
      badgeText,
      emoji: '🐾',
    }
  })

  const speciesDistribution = (() => {
    const counts = mascotasRecientes.reduce((acc, pet) => {
      const raw = String(pet?.especie || '').toLowerCase()
      if (raw.includes('can')) acc.caninos += 1
      else if (raw.includes('fel') || raw.includes('gat')) acc.felinos += 1
      else if (raw.includes('cone')) acc.conejos += 1
      else if (raw.includes('ave') || raw.includes('paja')) acc.aves += 1
      else acc.otros += 1
      return acc
    }, { caninos: 0, felinos: 0, conejos: 0, aves: 0, otros: 0 })

    const total = Object.values(counts).reduce((sum, v) => sum + v, 0)
    if (!total) {
      return [
        { label: '🐶 Caninos', value: 58, color: '#00c896' },
        { label: '🐱 Felinos', value: 29, color: '#4fc3f7' },
        { label: '🐰 Conejos', value: 7, color: '#f06292' },
        { label: '🐦 Aves', value: 4, color: '#ffb74d' },
        { label: '🐠 Otros', value: 2, color: '#b39ddb' },
      ]
    }

    return [
      { label: '🐶 Caninos', value: Math.round((counts.caninos / total) * 100), color: '#00c896' },
      { label: '🐱 Felinos', value: Math.round((counts.felinos / total) * 100), color: '#4fc3f7' },
      { label: '🐰 Conejos', value: Math.round((counts.conejos / total) * 100), color: '#f06292' },
      { label: '🐦 Aves', value: Math.round((counts.aves / total) * 100), color: '#ffb74d' },
      { label: '🐠 Otros', value: Math.max(0, Math.round((counts.otros / total) * 100)), color: '#b39ddb' },
    ]
  })()

  const monthlyConsultasData = {
    labels: miniBarLabels,
    datasets: [
      {
        label: 'Consultas',
        data: miniBarData,
        borderRadius: 6,
        borderSkipped: false,
        backgroundColor: miniBarData.map((_, idx) => (idx === 3 ? '#00c896' : 'rgba(0,200,150,0.55)')),
        hoverBackgroundColor: '#00e0a8',
        maxBarThickness: 20,
      },
    ],
  }

  const monthlyConsultasOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#1f2d40',
        borderColor: '#243246',
        borderWidth: 1,
        titleColor: '#e8edf5',
        bodyColor: '#e8edf5',
        padding: 10,
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: '#7a8fa6', font: { size: 10 } },
      },
      y: {
        beginAtZero: true,
        grid: { color: '#243246' },
        ticks: { color: '#7a8fa6', font: { size: 10 } },
      },
    },
  }

  const speciesChartData = {
    labels: speciesDistribution.map((item) => item.label.replace(/^[^\s]+\s/, '')),
    datasets: [
      {
        data: speciesDistribution.map((item) => item.value),
        backgroundColor: speciesDistribution.map((item) => item.color),
        borderColor: '#1a2535',
        borderWidth: 2,
        hoverOffset: 8,
      },
    ],
  }

  const speciesChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#1f2d40',
        borderColor: '#243246',
        borderWidth: 1,
        titleColor: '#e8edf5',
        bodyColor: '#e8edf5',
      },
    },
    cutout: '62%',
  }

  const qrTrendLabels = ['L', 'M', 'X', 'J', 'V', 'S', 'D']
  const qrTrendBase = miniBarData.slice(-7)
  const qrLineData = {
    labels: qrTrendLabels,
    datasets: [
      {
        label: 'Escaneos',
        data: qrTrendBase,
        borderColor: '#4fc3f7',
        pointBackgroundColor: '#4fc3f7',
        pointBorderColor: '#0f1623',
        pointRadius: 3,
        pointHoverRadius: 4,
        borderWidth: 2.5,
        tension: 0.35,
        fill: true,
        backgroundColor: 'rgba(79,195,247,0.10)',
      },
    ],
  }

  const qrLineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#1f2d40',
        borderColor: '#243246',
        borderWidth: 1,
        titleColor: '#e8edf5',
        bodyColor: '#e8edf5',
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: '#7a8fa6', font: { size: 10 } },
      },
      y: {
        grid: { color: '#243246' },
        ticks: { color: '#7a8fa6', font: { size: 10 } },
      },
    },
  }

  const myAppointments = recentAppointments.slice(0, 5)
  const myAppointmentsCompleted = myAppointments.filter((item) => item.badgeVariant === 'success').length
  const myAppointmentsPending = myAppointments.filter((item) => item.badgeVariant !== 'success').length
  const myAlertsCount = Math.max(0, (resumen.citasPendientes || 0))
  const vetDisplayName = [user?.nombre, user?.apellido].filter(Boolean).join(' ').trim() || 'Veterinario'
  const currentDateText = new Date().toLocaleDateString('es-ES', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  })

  if (isVeterinarioAdmin) {
    return (
      <div className="space-y-4">
        <div className="bg-[rgba(255,183,77,0.12)] border border-[rgba(255,183,77,0.25)] rounded-[10px] px-3.5 py-2.5 text-xs text-[#ffb74d] flex items-center gap-2">
          <Building2 className="w-4 h-4 shrink-0" />
          <span className="text-[#7a8fa6]">Mostrando datos de</span>
          <strong className="text-[#ffb74d]">{clinicName}</strong>
          {clinicAddress ? <span className="text-[#7a8fa6] truncate">— {clinicAddress}</span> : null}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
          <div className="bg-[#1a2535] border border-[#243246] rounded-[14px] p-4">
            <div className="w-9 h-9 rounded-[10px] bg-[rgba(0,200,150,0.12)] text-[#00c896] flex items-center justify-center text-base mb-3">🐾</div>
            <p className="text-[11px] text-[#7a8fa6] font-medium">Mascotas en mi clínica</p>
            <p className="text-[28px] leading-none font-semibold text-[#00c896] mt-1">{resumen.totalMascotas || 0}</p>
            <p className="text-[11px] text-[#7a8fa6] mt-2">{resumen.mascotasActivas || 0} vivas</p>
          </div>

          <div className="bg-[#1a2535] border border-[#243246] rounded-[14px] p-4">
            <div className="w-9 h-9 rounded-[10px] bg-[rgba(79,195,247,0.12)] text-[#4fc3f7] flex items-center justify-center text-base mb-3">📅</div>
            <p className="text-[11px] text-[#7a8fa6] font-medium">Citas hoy</p>
            <p className="text-[28px] leading-none font-semibold text-[#4fc3f7] mt-1">{resumen.totalCitas || 0}</p>
            <p className="text-[11px] text-[#7a8fa6] mt-2">{resumen.citasPendientes || 0} pendientes · {resumen.citasCompletadas || 0} completadas</p>
          </div>

          <div className="bg-[#1a2535] border border-[#243246] rounded-[14px] p-4">
            <div className="w-9 h-9 rounded-[10px] bg-[rgba(255,183,77,0.12)] text-[#ffb74d] flex items-center justify-center text-base mb-3">👨‍⚕️</div>
            <p className="text-[11px] text-[#7a8fa6] font-medium">Vets en mi clínica</p>
            <p className="text-[28px] leading-none font-semibold text-[#ffb74d] mt-1">{resumen.totalVeterinarios || 0}</p>
            <p className="text-[11px] text-[#7a8fa6] mt-2">{resumen.veterinariosActivos || 0} en turno hoy</p>
          </div>

          <div className="bg-[#1a2535] border border-[#243246] rounded-[14px] p-4">
            <div className="w-9 h-9 rounded-[10px] bg-[rgba(240,98,146,0.12)] text-[#f06292] flex items-center justify-center text-base mb-3">⭐</div>
            <p className="text-[11px] text-[#7a8fa6] font-medium">Satisfacción</p>
            <p className="text-[28px] leading-none font-semibold text-[#f06292] mt-1">4.9</p>
            <p className="text-[11px] text-[#7a8fa6] mt-2">Promedio del mes</p>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
          <div className="bg-[#1a2535] border border-[#243246] rounded-[14px] p-4">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-sm font-semibold text-[#e8edf5]">Consultas mensuales — {clinicName}</h3>
                <p className="text-xs text-[#7a8fa6]">Últimos 12 meses</p>
              </div>
              <Badge variant="warning">+8% ↑</Badge>
            </div>
            <div className="h-56">
              <Bar data={monthlyConsultasData} options={monthlyConsultasOptions} />
            </div>
          </div>

          <div className="bg-[#1a2535] border border-[#243246] rounded-[14px] p-4">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-sm font-semibold text-[#e8edf5]">Citas del día</h3>
                <p className="text-xs text-[#7a8fa6]">Agenda reciente de tu clínica</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => navigate('/citas')}>
                Ver todas
              </Button>
            </div>
            <div className="space-y-2">
              {recentAppointments.length ? (
                recentAppointments.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => navigate('/citas')}
                    className="w-full text-left flex items-center gap-2.5 p-2.5 rounded-[10px] bg-[#1f2d40] border border-transparent hover:border-[#243246] transition-colors"
                  >
                    <span className="text-lg">{item.emoji}</span>
                    <div className="min-w-0 flex-1">
                      <p className="text-[13px] font-medium text-[#e8edf5] truncate">{item.mascotaNombre}</p>
                      <p className="text-[11px] text-[#7a8fa6] truncate">{item.vetName} · {item.clinicName}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Badge variant={item.badgeVariant}>{item.badgeText}</Badge>
                      <span className="text-[11px] text-[#7a8fa6]">{item.hora}</span>
                    </div>
                  </button>
                ))
              ) : (
                <p className="text-xs text-[#7a8fa6]">Sin citas recientes por mostrar.</p>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
          <div className="bg-[#1a2535] border border-[#243246] rounded-[14px] p-4">
            <h3 className="text-sm font-semibold text-[#e8edf5] mb-3">Distribución de pacientes</h3>
            <div className="space-y-2.5">
              {speciesDistribution.map((item) => (
                <div key={item.label}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-[#e8edf5]">{item.label}</span>
                    <span style={{ color: item.color }}>{item.value}%</span>
                  </div>
                  <div className="h-[5px] rounded-full bg-[#243246] overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${item.value}%`, background: item.color }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-[#1a2535] border border-[#243246] rounded-[14px] p-4">
            <h3 className="text-sm font-semibold text-[#e8edf5] mb-3">Alertas de mi clínica</h3>
            <div className="space-y-2">
              <div className="flex gap-2 p-2.5 rounded-[9px] bg-[rgba(239,83,80,0.12)] border-l-[3px] border-[#ef5350]">
                <span>⚠️</span>
                <div>
                  <p className="text-xs font-semibold text-[#ef5350]">Vacunas pendientes</p>
                  <p className="text-[11px] text-[#7a8fa6]">{resumen.citasPendientes || 0} citas requieren acción</p>
                </div>
              </div>
              <div className="flex gap-2 p-2.5 rounded-[9px] bg-[rgba(255,183,77,0.12)] border-l-[3px] border-[#ffb74d]">
                <span>🔔</span>
                <div>
                  <p className="text-xs font-semibold text-[#ffb74d]">Seguimientos en curso</p>
                  <p className="text-[11px] text-[#7a8fa6]">{resumen.totalFichas || 0} fichas clínicas en control</p>
                </div>
              </div>
              <div className="flex gap-2 p-2.5 rounded-[9px] bg-[rgba(79,195,247,0.12)] border-l-[3px] border-[#4fc3f7]">
                <span>👨‍⚕️</span>
                <div>
                  <p className="text-xs font-semibold text-[#4fc3f7]">Veterinarios en turno</p>
                  <p className="text-[11px] text-[#7a8fa6]">{resumen.veterinariosActivos || 0} en turno</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-[#1a2535] border border-[#243246] rounded-[14px] p-4">
            <h3 className="text-sm font-semibold text-[#e8edf5] mb-3">Actividad clínica</h3>
            <div className="text-center py-2">
              <p className="text-[40px] leading-none font-bold text-[#00c896]">{resumen.totalUsuarios || 0}</p>
              <p className="text-xs text-[#7a8fa6] mb-3">cuentas activas registradas</p>
              <div className="h-24 mb-3">
                <Line data={qrLineData} options={qrLineOptions} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-[#1f2d40] rounded-[9px] p-2.5">
                  <p className="text-lg font-semibold text-[#4fc3f7]">{resumen.totalClinicas || 1}</p>
                  <p className="text-[10px] text-[#7a8fa6]">Clínica</p>
                </div>
                <div className="bg-[#1f2d40] rounded-[9px] p-2.5">
                  <p className="text-lg font-semibold text-[#f06292]">{resumen.totalMascotas || 0}</p>
                  <p className="text-[10px] text-[#7a8fa6]">Mascotas</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (isVeterinario && !isVeterinarioAdmin) {
    return (
      <div className="space-y-4">
        <div className="mb-1">
          <h2 className="text-[18px] font-semibold text-[#e8edf5]">Buen día, {vetDisplayName} 👋</h2>
          <p className="text-xs text-[#7a8fa6] capitalize">{currentDateText} · {clinicName}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="bg-[#1a2535] border border-[#243246] rounded-[14px] p-4">
            <div className="w-9 h-9 rounded-[10px] bg-[rgba(79,195,247,0.12)] text-[#4fc3f7] flex items-center justify-center text-base mb-3">📅</div>
            <p className="text-[11px] text-[#7a8fa6] font-medium">Mis citas hoy</p>
            <p className="text-[28px] leading-none font-semibold text-[#4fc3f7] mt-1">{myAppointments.length || resumen.totalCitas || 0}</p>
            <p className="text-[11px] text-[#7a8fa6] mt-2">{myAppointmentsCompleted} completadas · {myAppointmentsPending} pendientes</p>
          </div>

          <div className="bg-[#1a2535] border border-[#243246] rounded-[14px] p-4">
            <div className="w-9 h-9 rounded-[10px] bg-[rgba(0,200,150,0.12)] text-[#00c896] flex items-center justify-center text-base mb-3">🐾</div>
            <p className="text-[11px] text-[#7a8fa6] font-medium">Mascotas en la clínica</p>
            <p className="text-[28px] leading-none font-semibold text-[#00c896] mt-1">{resumen.totalMascotas || 0}</p>
            <p className="text-[11px] text-[#7a8fa6] mt-2">+{Math.max(0, Math.round((resumen.totalMascotas || 0) * 0.05))} esta semana</p>
          </div>

          <div className="bg-[#1a2535] border border-[#243246] rounded-[14px] p-4">
            <div className="w-9 h-9 rounded-[10px] bg-[rgba(255,183,77,0.12)] text-[#ffb74d] flex items-center justify-center text-base mb-3">⚠️</div>
            <p className="text-[11px] text-[#7a8fa6] font-medium">Alertas pendientes</p>
            <p className="text-[28px] leading-none font-semibold text-[#ffb74d] mt-1">{myAlertsCount}</p>
            <p className="text-[11px] text-[#7a8fa6] mt-2">Requieren atención</p>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
          <div className="bg-[#1a2535] border border-[#243246] rounded-[14px] p-4">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-sm font-semibold text-[#e8edf5]">Mi agenda de hoy</h3>
                <p className="text-xs text-[#7a8fa6]">Solo tus citas asignadas</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => navigate('/citas')}>
                Ver completa
              </Button>
            </div>
            <div className="space-y-2">
              {myAppointments.length ? (
                myAppointments.map((item) => (
                  <div key={item.id} className="flex gap-2.5 items-center rounded-[10px] bg-[#1f2d40] border border-[#243246] px-3 py-2.5">
                    <span className="text-lg">{item.emoji}</span>
                    <div className="min-w-0 flex-1">
                      <p className="text-[13px] font-medium text-[#e8edf5] truncate">{item.mascotaNombre}</p>
                      <p className="text-[11px] text-[#7a8fa6] truncate">{item.clinicName}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Badge variant={item.badgeVariant}>{item.badgeText}</Badge>
                      <span className="text-[11px] text-[#7a8fa6]">{item.hora}</span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-xs text-[#7a8fa6]">Sin citas asignadas hoy.</p>
              )}
            </div>
          </div>

          <div className="space-y-3">
            <div className="bg-[#1a2535] border border-[#243246] rounded-[14px] p-4">
              <h3 className="text-sm font-semibold text-[#e8edf5] mb-3">Alertas de mis pacientes</h3>
              <div className="space-y-2">
                <div className="flex gap-2 p-2.5 rounded-[9px] bg-[rgba(239,83,80,0.12)] border-l-[3px] border-[#ef5350]">
                  <span>⚠️</span>
                  <div>
                    <p className="text-xs font-semibold text-[#ef5350]">Vacuna vencida</p>
                    <p className="text-[11px] text-[#7a8fa6]">{myAlertsCount || 0} pacientes con seguimiento pendiente</p>
                  </div>
                </div>
                <div className="flex gap-2 p-2.5 rounded-[9px] bg-[rgba(255,183,77,0.12)] border-l-[3px] border-[#ffb74d]">
                  <span>💊</span>
                  <div>
                    <p className="text-xs font-semibold text-[#ffb74d]">Medicación en curso</p>
                    <p className="text-[11px] text-[#7a8fa6]">{resumen.totalFichas || 0} fichas en control</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-[#1a2535] border border-[#243246] rounded-[14px] p-4">
              <h3 className="text-sm font-semibold text-[#e8edf5] mb-3">Mis estadísticas del mes</h3>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-[#1f2d40] rounded-[10px] p-3 text-center">
                  <p className="text-[22px] leading-none font-semibold text-[#00c896]">{resumen.totalCitas || 0}</p>
                  <p className="text-[10px] text-[#7a8fa6] mt-1">Consultas</p>
                </div>
                <div className="bg-[#1f2d40] rounded-[10px] p-3 text-center">
                  <p className="text-[22px] leading-none font-semibold text-[#4fc3f7]">{resumen.totalMascotas || 0}</p>
                  <p className="text-[10px] text-[#7a8fa6] mt-1">Pacientes</p>
                </div>
                <div className="bg-[#1f2d40] rounded-[10px] p-3 text-center">
                  <p className="text-[22px] leading-none font-semibold text-[#f06292]">4.8</p>
                  <p className="text-[10px] text-[#7a8fa6] mt-1">Satisfacción</p>
                </div>
                <div className="bg-[#1f2d40] rounded-[10px] p-3 text-center">
                  <p className="text-[22px] leading-none font-semibold text-[#ffb74d]">32m</p>
                  <p className="text-[10px] text-[#7a8fa6] mt-1">Promedio</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Dashboard simplificado para DUEÑO
  if (isDueno) {
    try {
      return (
        <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 animate-fade-in-down bg-white/95 border border-pastel-pink rounded-3xl p-4 sm:p-5 shadow-soft">
          <div className="min-w-0 flex-1">
            <p className="text-xs uppercase tracking-wider text-pastel-lavender font-semibold">Pet Health Dashboard</p>
            <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-pastel-ink via-primary-600 to-pastel-ink dark:from-white dark:via-primary-400 dark:to-white bg-clip-text text-transparent truncate animate-fade-in">
              {getGreeting()}, {user?.nombre || 'Usuario'}
            </h1>
            <p className="mt-1 text-xs sm:text-sm text-slate-500 dark:text-gray-400 animate-fade-in" style={{ animationDelay: '100ms' }}>
              Resumen de tus mascotas y actividades
            </p>
          </div>
          <button
            onClick={refetch}
            className="btn-secondary inline-flex items-center w-full sm:w-auto justify-center animate-fade-in-up"
            style={{ animationDelay: '200ms' }}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualizar
          </button>
        </div>

        {/* Stats Grid Simplificado - Solo 4 tarjetas */}
        <StatsGrid>
          <StatsCard
            title="Mis Mascotas"
            value={resumen.totalMascotas || 0}
            icon={<Heart className="w-6 h-6" />}
            color="purple"
            subtitle={`${resumen.mascotasActivas || 0} vivas`}
          />
          <StatsCard
            title="Mis Citas"
            value={resumen.totalCitas || 0}
            icon={<Calendar className="w-6 h-6" />}
            color="blue"
            subtitle={`${resumen.citasPendientes || 0} pendientes`}
          />
          <StatsCard
            title="Fichas Clínicas"
            value={resumen.totalFichas || 0}
            icon={<FileText className="w-6 h-6" />}
            color="green"
            subtitle="Historiales médicos"
          />
          <StatsCard
            title="Notificaciones"
            value={isDueno ? 0 : contadorNoLeidas}
            icon={<Bell className="w-6 h-6" />}
            color="orange"
            subtitle={isDueno ? "Ver en menú" : (contadorNoLeidas > 0 ? `${contadorNoLeidas} sin leer` : "Todas leídas")}
          />
        </StatsGrid>

        {/* Contenido Principal - Grid 2 columnas */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Citas Recientes */}
          <Card className="card-hover">
            <div className="px-6 py-4 border-b border-pastel-pink dark:border-gray-700 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                <Calendar className="w-5 h-5 mr-2 text-primary-600 dark:text-primary-400" />
                Mis Citas Recientes
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/citas')}
              >
                Ver todas
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
            <div className="p-6">
              {citasRecientes.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-center py-4">No hay citas recientes</p>
              ) : (
                <div className="space-y-3">
                  {citasRecientes.slice(0, 5).map((cita) => {
                    if (!cita) return null
                    const citaId = cita.id_cita || cita.idCita
                    if (!citaId) return null
                    
                    const mascotaNombre = cita.mascota?.nombre || cita.mascota?.nombre || 'Sin mascota'
                    const fechaCita = cita.fecha_cita_propuesta || cita.fechaCitaPropuesta
                    let fechaFormateada = 'Sin fecha'
                    try {
                      if (fechaCita) {
                        fechaFormateada = new Date(fechaCita).toLocaleDateString('es-ES')
                      }
                    } catch (e) {
                      console.warn('Error formateando fecha:', e)
                    }
                    
                    const estado = cita.estado || ''
                    const estadoTexto = estado ? estado.replace('_', ' ') : ''
                    
                    return (
                      <div key={citaId} className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer" onClick={() => navigate('/citas')}>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {mascotaNombre}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {fechaFormateada}
                          </p>
                        </div>
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          estado === 'APROBADA' || estado === 'COMPLETADA' 
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                            : estado === 'PENDIENTE_APROBACION'
                            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                        }`}>
                          {estadoTexto}
                        </span>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </Card>

          {/* Mascotas Recientes */}
          <Card className="card-hover">
            <div className="px-6 py-4 border-b border-pastel-pink dark:border-gray-700 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                <Heart className="w-5 h-5 mr-2 text-primary-600 dark:text-primary-400" />
                Mis Mascotas
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/mascotas')}
              >
                Ver todas
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
            <div className="p-6">
              {mascotasRecientes.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-center py-4">No hay mascotas registradas</p>
              ) : (
                <div className="space-y-3">
                  {mascotasRecientes.slice(0, 5).map((mascota) => {
                    if (!mascota) return null
                    const mascotaId = mascota.id_mascota || mascota.idMascota
                    if (!mascotaId) return null
                    
                    const nombre = mascota.nombre || 'Sin nombre'
                    const especie = mascota.especie || ''
                    const raza = mascota.raza || ''
                    const especieTexto = especie ? (raza ? `${especie} - ${raza}` : especie) : 'Sin especie'
                    const estado = mascota.estado || ''
                    
                    return (
                      <div key={mascotaId} className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer" onClick={() => navigate('/mascotas')}>
                        <div className="flex items-center space-x-3 flex-1">
                          <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/20 flex items-center justify-center">
                            <Heart className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {nombre}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {especieTexto}
                            </p>
                          </div>
                        </div>
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          estado === 'ACTIVA'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                            : estado === 'FALLECIDA'
                              ? 'bg-rose-100 text-rose-800 dark:bg-rose-900/20 dark:text-rose-300'
                              : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                        }`}>
                          {labelEstadoMascota(estado)}
                        </span>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </Card>

        </div>
      </div>
        )
    } catch (error) {
      console.error('Error renderizando dashboard DUEÑO:', error)
      return (
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <AlertCircle className="h-10 w-10 mx-auto text-red-600 dark:text-red-400 mb-4" />
            <p className="text-red-600 dark:text-red-400 mb-4">Error al cargar el dashboard</p>
            <button 
              onClick={() => window.location.reload()}
              className="btn-primary"
            >
              Recargar página
            </button>
          </div>
        </div>
      )
    }
  }

  if (isSuperAdmin) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
          <div className="bg-[#1a2535] border border-[#243246] rounded-[14px] p-4">
            <div className="w-9 h-9 rounded-[10px] bg-[rgba(0,200,150,0.12)] text-[#00c896] flex items-center justify-center text-base mb-3">🐾</div>
            <p className="text-[11px] text-[#7a8fa6] font-medium">Total Mascotas</p>
            <p className="text-[28px] leading-none font-semibold text-[#00c896] mt-1">{resumen.totalMascotas || 0}</p>
            <p className="text-[11px] text-[#7a8fa6] mt-2">
              {resumen.mascotasActivas || 0} vivas
            </p>
          </div>

          <div className="bg-[#1a2535] border border-[#243246] rounded-[14px] p-4">
            <div className="w-9 h-9 rounded-[10px] bg-[rgba(79,195,247,0.12)] text-[#4fc3f7] flex items-center justify-center text-base mb-3">📅</div>
            <p className="text-[11px] text-[#7a8fa6] font-medium">Citas Hoy</p>
            <p className="text-[28px] leading-none font-semibold text-[#4fc3f7] mt-1">{resumen.totalCitas || 0}</p>
            <p className="text-[11px] text-[#7a8fa6] mt-2">{resumen.citasPendientes || 0} pendientes · {resumen.citasCompletadas || 0} completadas</p>
          </div>

          <div className="bg-[#1a2535] border border-[#243246] rounded-[14px] p-4">
            <div className="w-9 h-9 rounded-[10px] bg-[rgba(240,98,146,0.12)] text-[#f06292] flex items-center justify-center text-base mb-3">🏥</div>
            <p className="text-[11px] text-[#7a8fa6] font-medium">Clínicas con suscripción</p>
            <p className="text-[28px] leading-none font-semibold text-[#f06292] mt-1">{resumen.clinicasActivas || resumen.totalClinicas || 0}</p>
            <p className="text-[11px] text-[#7a8fa6] mt-2">Total registradas: {resumen.totalClinicas || 0}</p>
          </div>

          <div className="bg-[#1a2535] border border-[#243246] rounded-[14px] p-4">
            <div className="w-9 h-9 rounded-[10px] bg-[rgba(255,183,77,0.12)] text-[#ffb74d] flex items-center justify-center text-base mb-3">👨‍⚕️</div>
            <p className="text-[11px] text-[#7a8fa6] font-medium">Veterinarios</p>
            <p className="text-[28px] leading-none font-semibold text-[#ffb74d] mt-1">{resumen.totalVeterinarios || 0}</p>
            <p className="text-[11px] text-[#7a8fa6] mt-2">{resumen.veterinariosActivos || 0} en turno hoy</p>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
          <div className="bg-[#1a2535] border border-[#243246] rounded-[14px] p-4">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-sm font-semibold text-[#e8edf5]">Consultas del mes</h3>
                <p className="text-xs text-[#7a8fa6]">Actividad consolidada</p>
              </div>
              <Badge variant="success">+8% ↑</Badge>
            </div>
            <div className="h-56">
              <Bar data={monthlyConsultasData} options={monthlyConsultasOptions} />
            </div>
          </div>

          <div className="bg-[#1a2535] border border-[#243246] rounded-[14px] p-4">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-sm font-semibold text-[#e8edf5]">Próximas citas</h3>
                <p className="text-xs text-[#7a8fa6]">Agenda reciente</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => navigate('/citas')}>
                Ver todas
              </Button>
            </div>
            <div className="space-y-2">
              {recentAppointments.length ? (
                recentAppointments.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => navigate('/citas')}
                    className="w-full text-left flex items-center gap-2.5 p-2.5 rounded-[10px] bg-[#1f2d40] border border-transparent hover:border-[#243246] transition-colors"
                  >
                    <span className="text-lg">{item.emoji}</span>
                    <div className="min-w-0 flex-1">
                      <p className="text-[13px] font-medium text-[#e8edf5] truncate">{item.mascotaNombre}</p>
                      <p className="text-[11px] text-[#7a8fa6] truncate">{item.vetName} · {item.clinicName}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Badge variant={item.badgeVariant}>{item.badgeText}</Badge>
                      <span className="text-[11px] text-[#7a8fa6]">{item.hora}</span>
                    </div>
                  </button>
                ))
              ) : (
                <p className="text-xs text-[#7a8fa6]">Sin citas recientes por mostrar.</p>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
          <div className="bg-[#1a2535] border border-[#243246] rounded-[14px] p-4">
            <h3 className="text-sm font-semibold text-[#e8edf5] mb-3">Distribución por especie</h3>
            <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-4 items-center">
              <div className="h-40">
                <Doughnut data={speciesChartData} options={speciesChartOptions} />
              </div>
              <div className="space-y-2.5">
                {speciesDistribution.map((item) => (
                  <div key={item.label}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-[#e8edf5]">{item.label}</span>
                      <span style={{ color: item.color }}>{item.value}%</span>
                    </div>
                    <div className="h-[5px] rounded-full bg-[#243246] overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${item.value}%`, background: item.color }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-[#1a2535] border border-[#243246] rounded-[14px] p-4">
            <h3 className="text-sm font-semibold text-[#e8edf5] mb-3">Alertas recientes</h3>
            <div className="space-y-2">
              <div className="flex gap-2 p-2.5 rounded-[9px] bg-[rgba(239,83,80,0.12)] border-l-[3px] border-[#ef5350]">
                <span>⚠️</span>
                <div>
                  <p className="text-xs font-semibold text-[#ef5350]">Vacunas pendientes</p>
                  <p className="text-[11px] text-[#7a8fa6]">{resumen.citasPendientes || 0} citas requieren acción</p>
                </div>
              </div>
              <div className="flex gap-2 p-2.5 rounded-[9px] bg-[rgba(255,183,77,0.12)] border-l-[3px] border-[#ffb74d]">
                <span>🔔</span>
                <div>
                  <p className="text-xs font-semibold text-[#ffb74d]">Seguimientos en curso</p>
                  <p className="text-[11px] text-[#7a8fa6]">{resumen.totalFichas || 0} fichas clínicas en control</p>
                </div>
              </div>
              <div className="flex gap-2 p-2.5 rounded-[9px] bg-[rgba(79,195,247,0.12)] border-l-[3px] border-[#4fc3f7]">
                <span>💊</span>
                <div>
                  <p className="text-xs font-semibold text-[#4fc3f7]">Actividad médica</p>
                  <p className="text-[11px] text-[#7a8fa6]">{resumen.citasCompletadas || 0} consultas completadas</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-[#1a2535] border border-[#243246] rounded-[14px] p-4">
            <h3 className="text-sm font-semibold text-[#e8edf5] mb-3">Accesos QR hoy</h3>
            <div className="text-center py-2">
              <p className="text-[40px] leading-none font-bold text-[#00c896]">{resumen.totalUsuarios || 0}</p>
              <p className="text-xs text-[#7a8fa6] mb-3">eventos de acceso registrados</p>
              <div className="h-24 mb-3">
                <Line data={qrLineData} options={qrLineOptions} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-[#1f2d40] rounded-[9px] p-2.5">
                  <p className="text-lg font-semibold text-[#4fc3f7]">{resumen.totalClinicas || 0}</p>
                  <p className="text-[10px] text-[#7a8fa6]">Clínicas</p>
                </div>
                <div className="bg-[#1f2d40] rounded-[9px] p-2.5">
                  <p className="text-lg font-semibold text-[#f06292]">{resumen.totalMascotas || 0}</p>
                  <p className="text-[10px] text-[#7a8fa6]">Mascotas</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Dashboard completo para ADMIN, VETERINARIO, etc.
  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 animate-fade-in-down bg-white/95 border border-pastel-pink rounded-3xl p-4 sm:p-5 shadow-soft">
        <div className="min-w-0 flex-1">
          <p className="text-xs uppercase tracking-wider text-pastel-lavender font-semibold">Control Center</p>
          <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-pastel-ink via-primary-600 to-pastel-ink dark:from-white dark:via-primary-400 dark:to-white bg-clip-text text-transparent animate-fade-in">
            {getGreeting()}, Bienvenido
          </h1>
          <p className="mt-1 text-xs sm:text-sm text-slate-500 dark:text-gray-400 animate-fade-in" style={{ animationDelay: '100ms' }}>
            Aquí tienes un resumen completo de tu sistema veterinario
          </p>
        </div>
        <button
          onClick={refetch}
          className="btn-secondary inline-flex items-center w-full sm:w-auto justify-center animate-fade-in-up"
          style={{ animationDelay: '200ms' }}
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Actualizar
        </button>
      </div>

      {/* Stats Grid Principal */}
      <StatsGrid>
        <StatsCard
          title="Total Mascotas"
          value={resumen.totalMascotas || 0}
          icon={<Heart className="w-6 h-6" />}
          color="purple"
          subtitle={`${resumen.mascotasActivas || 0} vivas`}
          trend={resumen.mascotasActivas > 0 ? '+12%' : null}
        />
        <StatsCard
          title="Total Citas"
          value={resumen.totalCitas || 0}
          icon={<Calendar className="w-6 h-6" />}
          color="blue"
          subtitle={`${resumen.citasCompletadas || 0} completadas`}
          trend={resumen.citasCompletadas > 0 ? '+8%' : null}
        />
        <StatsCard
          title="Fichas Clínicas"
          value={resumen.totalFichas || 0}
          icon={<FileText className="w-6 h-6" />}
          color="green"
          subtitle="Historiales médicos"
          trend={resumen.totalFichas > 0 ? '+5%' : null}
        />
        {!isVeterinario && (
          <StatsCard
            title="Veterinarios"
            value={resumen.totalVeterinarios || 0}
            icon={<Users className="w-6 h-6" />}
            color="indigo"
            subtitle={`${resumen.veterinariosActivos || 0} en turno`}
          />
        )}
        {isVeterinario && (
          <StatsCard
            title="Mi Clínica"
            value={resumen.totalClinicas || 0}
            icon={<Building2 className="w-6 h-6" />}
            color="indigo"
            subtitle={`${resumen.clinicasActivas || 0} con suscripción vigente`}
          />
        )}
      </StatsGrid>

      {/* Stats Secundarias */}
      <div className="grid grid-cols-1 gap-4 sm:gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <StatsCard
          title="Clínicas"
          value={resumen.totalClinicas || 0}
          icon={<Building2 className="w-5 h-5" />}
          color="yellow"
          subtitle={`${resumen.clinicasActivas || 0} con suscripción vigente`}
        />
        <StatsCard
          title="Usuarios"
          value={resumen.totalUsuarios || 0}
          icon={<User className="w-5 h-5" />}
          color="purple"
          subtitle="Registrados"
        />
        <StatsCard
          title="Citas Pendientes"
          value={resumen.citasPendientes || 0}
          icon={<Clock className="w-5 h-5" />}
          color="orange"
          subtitle="Por aprobar"
        />
        <StatsCard
          title="Actividad"
          value="98%"
          icon={<Activity className="w-5 h-5" />}
          color="green"
          subtitle="Uptime del sistema"
        />
      </div>

      {/* Tablas de Datos Recientes */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Citas Recientes */}
        <Card className="card-hover">
          <div className="px-6 py-4 border-b border-pastel-pink dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
              <Calendar className="w-5 h-5 mr-2 text-primary-600 dark:text-primary-400" />
              Citas Recientes
            </h2>
          </div>
          <div className="p-6">
            {citasRecientes.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 text-center py-4">No hay citas recientes</p>
            ) : (
              <div className="space-y-3">
                {citasRecientes.slice(0, 5).map((cita) => (
                  <div key={cita.id_cita || cita.idCita} className="flex items-center justify-between p-3 rounded-xl border border-pastel-pink/60 hover:bg-pastel-cream dark:hover:bg-gray-700 transition-colors">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {cita.mascota?.nombre || 'Sin mascota'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {cita.fecha_cita_propuesta || cita.fechaCitaPropuesta 
                          ? new Date(cita.fecha_cita_propuesta || cita.fechaCitaPropuesta).toLocaleDateString('es-ES')
                          : 'Sin fecha'}
                      </p>
                    </div>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      cita.estado === 'APROBADA' || cita.estado === 'COMPLETADA' 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                        : cita.estado === 'PENDIENTE_APROBACION'
                        ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                    }`}>
                      {cita.estado?.replace('_', ' ')}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>

        {/* Mascotas Recientes */}
        <Card className="card-hover">
          <div className="px-6 py-4 border-b border-pastel-pink dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
              <Heart className="w-5 h-5 mr-2 text-primary-600 dark:text-primary-400" />
              Mascotas Recientes
            </h2>
          </div>
          <div className="p-6">
            {mascotasRecientes.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 text-center py-4">No hay mascotas recientes</p>
            ) : (
              <div className="space-y-3">
                {mascotasRecientes.slice(0, 5).map((mascota) => (
                  <div key={mascota.id_mascota || mascota.idMascota} className="flex items-center justify-between p-3 rounded-xl border border-pastel-pink/60 hover:bg-pastel-cream dark:hover:bg-gray-700 transition-colors">
                    <div className="flex items-center space-x-3 flex-1">
                      <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/20 flex items-center justify-center">
                        <Heart className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {mascota.nombre}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {mascota.especie} {mascota.raza && `- ${mascota.raza}`}
                        </p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      mascota.estado === 'ACTIVA'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                        : mascota.estado === 'FALLECIDA'
                          ? 'bg-rose-100 text-rose-800 dark:bg-rose-900/20 dark:text-rose-300'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                    }`}>
                      {labelEstadoMascota(mascota.estado)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>

      </div>
    </div>
  )
}
